import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  appendVideoconsultationSummaryToParams,
  buildVideoconsultationRecommendationCopy,
  formatVideoconsultationSummaryLabel,
  readVideoconsultationSummaryFromQuery,
} from './videoconsultationSummary';

const CARE_PROVIDER_TYPE_OPTIONS = [
  { value: '', label: 'Any clinic type' },
  { value: 'clinic', label: 'Clinic' },
  { value: 'physiotherapist', label: 'Physiotherapist' },
  { value: 'public_rehabilitation_center', label: 'Public rehabilitation center' },
  { value: 'private_care_provider', label: 'Private care provider' },
  { value: 'eldercare_provider', label: 'Eldercare provider' },
  { value: 'fitness_center', label: 'Fitness center' },
  { value: 'insurance_company', label: 'Insurance company' },
  { value: 'hospital', label: 'Hospital' },
];

const useStyles = makeStyles(() => ({
  root: {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #eef3f7 0%, #dce4eb 100%)',
    padding: '40px 16px 64px',
  },
  panel: {
    maxWidth: 1180,
    margin: '0 auto',
  },
  introBox: {
    background: 'linear-gradient(180deg, rgba(220, 228, 235, 0.85) 0%, rgba(255, 255, 255, 0.95) 100%)',
    borderRadius: 18,
    padding: 20,
    marginBottom: 24,
  },
  recommendationBox: {
    borderRadius: 18,
    marginBottom: 18,
    padding: 18,
  },
  filterBox: {
    background: '#ffffff',
    borderRadius: 18,
    padding: 20,
    boxShadow: '0 12px 28px rgba(0, 45, 85, 0.08)',
    marginBottom: 24,
  },
  resultCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 18,
    boxShadow: '0 14px 30px rgba(0, 45, 85, 0.10)',
  },
  chip: {
    marginRight: 8,
    marginBottom: 8,
  },
  primaryButton: {
    background: '#fb741a',
    color: '#fff',
    fontWeight: 600,
    borderRadius: 18,
    '&:hover': {
      background: '#002D55',
    },
  },
  secondaryButton: {
    border: '1px solid #002D55',
    color: '#002D55',
    background: 'transparent',
    fontWeight: 600,
    borderRadius: 18,
  },
  sectionTitle: {
    color: '#002D55',
    fontWeight: 700,
    marginBottom: 14,
    textAlign: 'center',
  },
  fieldControl: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 18,
      backgroundColor: '#ffffff',
    },
  },
  helperText: {
    color: '#486d86',
    marginBottom: 16,
  },
  selectionSummary: {
    color: '#486d86',
    marginBottom: 16,
  },
}));

const parseSpecialities = (value) => (
  (value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
);

const formatSpecialityLabel = (value) => value.replace(/_/g, ' ');

const formatSlotDate = (value) => {
  if (!value) {
    return 'No slot available yet';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString();
};

function PublicProviderMatch() {
  const classes = useStyles();
  const query = useMemo(() => new URLSearchParams(window.location.search), []);
  const sessionId = query.get('session_id') || '';
  const intakeHandoffId = query.get('intake_handoff_id') || '';
  const clinicId = query.get('clinic_id') || '';
  const clinicName = query.get('clinic_name') || '';
  const lockClinic = query.get('lock_clinic') === 'true';
  const temporaryTestMode = query.get('temporary_test_mode') === 'true';
  const [specialities] = useState(() => parseSpecialities(query.get('specialities')));
  const recommendationSummary = useMemo(() => readVideoconsultationSummaryFromQuery(query), [query]);
  const recommendationCopy = useMemo(
    () => buildVideoconsultationRecommendationCopy(recommendationSummary),
    [recommendationSummary],
  );
  const [careProviderType, setCareProviderType] = useState(query.get('care_provider_type') || '');
  const [city, setCity] = useState(query.get('city') || '');
  const [country, setCountry] = useState(query.get('country') || '');
  const [addressQuery, setAddressQuery] = useState(query.get('address_query') || '');
  const [requiresInsurance, setRequiresInsurance] = useState(false);
  const [insuranceNetwork, setInsuranceNetwork] = useState('');
  const [requiresPublicSubsidy, setRequiresPublicSubsidy] = useState(false);
  const [availabilityWindow, setAvailabilityWindow] = useState('');
  const [availableFrom, setAvailableFrom] = useState('');
  const [availableTo, setAvailableTo] = useState('');
  const [insuranceCatalog, setInsuranceCatalog] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const backendBaseUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';
  const selectedSummary = useMemo(
    () => specialities.map((speciality) => formatSpecialityLabel(speciality)).join(', '),
    [specialities],
  );

  useEffect(() => {
    if (!lockClinic || !clinicId) {
      return;
    }

    const bookingParams = new URLSearchParams(window.location.search);
    bookingParams.set('clinic_id', clinicId);
    bookingParams.set('lock_clinic', 'true');
    if (intakeHandoffId) {
      bookingParams.set('intake_handoff_id', intakeHandoffId);
    }
    window.location.replace(`/public/booking?${bookingParams.toString()}`);
  }, [clinicId, intakeHandoffId, lockClinic]);

  useEffect(() => {
    let cancelled = false;

    const fetchInsuranceCatalog = async () => {
      try {
        const response = await fetch(
          `${backendBaseUrl}/api/profile/public/insurance-support/catalog/?country=${encodeURIComponent(country || 'Denmark')}`,
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok || cancelled) {
          return;
        }
        setInsuranceCatalog(Array.isArray(data.insurance_support_catalog?.network_options) ? data.insurance_support_catalog.network_options : []);
      } catch {
        if (!cancelled) {
          setInsuranceCatalog([]);
        }
      }
    };

    fetchInsuranceCatalog();
    return () => {
      cancelled = true;
    };
  }, [backendBaseUrl, country]);

  const fetchMatches = async () => {
    if (specialities.length === 0) {
      setMatches([]);
      setError('We could not derive a matching speciality from the completed assessment yet.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        specialities,
      };

      if (intakeHandoffId) {
        payload.public_intake_handoff_id = intakeHandoffId;
      }

      if (temporaryTestMode) {
        payload.temporary_test_mode = true;
      }

      if (city.trim()) {
        payload.city = city.trim();
      }
      if (country.trim()) {
        payload.country = country.trim();
      }
      if (careProviderType) {
        payload.care_provider_type = careProviderType;
      }
      if (addressQuery.trim()) {
        payload.address_query = addressQuery.trim();
      }
      if (requiresInsurance || insuranceNetwork) {
        payload.require_insurance_support = true;
      }
      if (insuranceNetwork) {
        payload.insurance_network = insuranceNetwork;
      }
      if (requiresPublicSubsidy) {
        payload.public_subsidy = true;
      }
      if (availabilityWindow) {
        payload.availability_window = availabilityWindow;
      }
      if (availabilityWindow === 'custom') {
        if (!availableFrom || !availableTo) {
          throw new Error('Choose both custom availability dates before refreshing matches.');
        }
        payload.available_from = availableFrom;
        payload.available_to = availableTo;
      }

      const response = await fetch(`${backendBaseUrl}/api/profile/public/careproviders/match/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load matching clinics.');
      }

      setMatches(Array.isArray(data.care_providers) ? data.care_providers : []);
    } catch (matchError) {
      setMatches([]);
      setError(matchError.message || 'Failed to load matching clinics.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (lockClinic && clinicId) {
      return;
    }

    fetchMatches();
  }, [clinicId, lockClinic]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleContinueToBooking = (clinic) => {
    const bookingParams = new URLSearchParams();
    if (sessionId) {
      bookingParams.set('session_id', sessionId);
    }
    if (intakeHandoffId) {
      bookingParams.set('intake_handoff_id', intakeHandoffId);
    }
    if (temporaryTestMode) {
      bookingParams.set('temporary_test_mode', 'true');
    }
    bookingParams.set('clinic_id', clinic.id);
    bookingParams.set('clinic_name', clinic.name_business || 'Selected clinic');
    bookingParams.set('city', clinic.city || '');
    bookingParams.set('country', clinic.country || '');
    if (specialities.length > 0) {
      bookingParams.set('specialities', specialities.join(','));
    }
    if (careProviderType) {
      bookingParams.set('care_provider_type', careProviderType);
    }
    if (addressQuery.trim()) {
      bookingParams.set('address_query', addressQuery.trim());
    }
    if (availabilityWindow) {
      bookingParams.set('availability_window', availabilityWindow);
    }
    if (availableFrom) {
      bookingParams.set('available_from', availableFrom);
    }
    if (availableTo) {
      bookingParams.set('available_to', availableTo);
    }
    if (requiresInsurance) {
      bookingParams.set('require_insurance_support', 'true');
    }
    if (insuranceNetwork) {
      bookingParams.set('insurance_network', insuranceNetwork);
    }
    if (requiresPublicSubsidy) {
      bookingParams.set('public_subsidy', 'true');
    }
    appendVideoconsultationSummaryToParams(bookingParams, recommendationSummary);

    const slot = clinic.earliest_available_slot;
    if (slot?.start_datetime) {
      bookingParams.set('slot_start', slot.start_datetime);
    }
    if (slot?.end_datetime) {
      bookingParams.set('slot_end', slot.end_datetime);
    }
    if (slot?.clinic_location_name) {
      bookingParams.set('location_name', slot.clinic_location_name);
    }
    if (slot?.clinic_location_id) {
      bookingParams.set('clinic_location_id', slot.clinic_location_id);
    }

    window.location.assign(`/public/booking?${bookingParams.toString()}`);
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.panel}>
        <Box className={classes.introBox}>
          <Typography variant="h5" style={{ color: '#002D55', fontWeight: 700, marginBottom: 8, textAlign: 'center' }}>
            {lockClinic && clinicId ? `Redirecting to ${clinicName || 'your clinic'} booking` : 'Find the right clinic for your assessment'}
          </Typography>
          <Typography variant="body2" style={{ color: '#486d86', textAlign: 'center' }}>
            {lockClinic && clinicId
              ? 'This link is locked to one clinic, so other clinic recommendations are skipped.'
              : 'Use the digital intake results together with location, availability, and insurer filters to choose a clinic before continuing to booking.'}
          </Typography>
        </Box>

        {recommendationCopy && (
          <Box
            className={classes.recommendationBox}
            style={{
              background: recommendationCopy.background,
              border: `1px solid ${recommendationCopy.border}`,
            }}
          >
            <Typography variant="subtitle1" style={{ color: recommendationCopy.accent, fontWeight: 700, marginBottom: 8 }}>
              {recommendationCopy.title}
            </Typography>
            <Typography variant="body2" style={{ color: '#324158', marginBottom: 12 }}>
              {recommendationCopy.body}
            </Typography>
            <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
              {(recommendationSummary?.reportedSpecialities || []).map((item) => (
                <Chip key={`match-speciality-${item}`} label={formatVideoconsultationSummaryLabel(item)} className={classes.chip} />
              ))}
              {(recommendationSummary?.redFlagTags || []).map((item) => (
                <Chip
                  key={`match-redflag-${item}`}
                  label={formatVideoconsultationSummaryLabel(item)}
                  className={classes.chip}
                  style={{ backgroundColor: '#fff', border: '1px solid #e2b27b' }}
                />
              ))}
            </Box>
          </Box>
        )}

        <Box className={classes.filterBox}>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Choose clinic preferences
          </Typography>
          {recommendationCopy && (
            <Typography variant="body2" className={classes.helperText}>
              The recommendations above come from the completed digital intake. You can narrow clinics further before moving into booking.
            </Typography>
          )}
          <Box style={{ marginBottom: 12 }}>
            {specialities.map((speciality) => (
              <Chip key={speciality} label={formatSpecialityLabel(speciality)} className={classes.chip} />
            ))}
            {specialities.length === 0 && (
              <Chip label="No derived speciality yet" className={classes.chip} />
            )}
          </Box>
          <Typography variant="body2" className={classes.selectionSummary}>
            Selected specialities: {selectedSummary || 'none yet'}
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined" className={classes.fieldControl}>
                <InputLabel id="care-provider-type-label">Clinic type</InputLabel>
                <Select
                  labelId="care-provider-type-label"
                  value={careProviderType}
                  onChange={(event) => setCareProviderType(event.target.value)}
                  label="Clinic type"
                >
                  {CARE_PROVIDER_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value || 'any'} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="City"
                variant="outlined"
                value={city}
                className={classes.fieldControl}
                onChange={(event) => setCity(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                label="Typed address or postcode"
                variant="outlined"
                value={addressQuery}
                className={classes.fieldControl}
                onChange={(event) => setAddressQuery(event.target.value)}
                helperText="Fast proximity ranking uses the typed address together with clinic postcode and city data."
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Country"
                variant="outlined"
                value={country}
                className={classes.fieldControl}
                onChange={(event) => setCountry(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined" className={classes.fieldControl}>
                <InputLabel id="availability-window-label">Availability</InputLabel>
                <Select
                  labelId="availability-window-label"
                  label="Availability"
                  value={availabilityWindow}
                  onChange={(event) => setAvailabilityWindow(event.target.value)}
                >
                  <MenuItem value="">Any upcoming time</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="within_3_days">Within 3 days</MenuItem>
                  <MenuItem value="within_7_days">Within 7 days</MenuItem>
                  <MenuItem value="custom">Custom interval</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {availabilityWindow === 'custom' && (
              <>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Available from"
                    variant="outlined"
                    value={availableFrom}
                    className={classes.fieldControl}
                    onChange={(event) => setAvailableFrom(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Available to"
                    variant="outlined"
                    value={availableTo}
                    className={classes.fieldControl}
                    onChange={(event) => setAvailableTo(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="contained"
                className={classes.primaryButton}
                onClick={fetchMatches}
                disabled={loading}
                style={{ minHeight: 56 }}
              >
                Refresh Matches
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={requiresInsurance}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setRequiresInsurance(checked);
                      if (!checked) {
                        setInsuranceNetwork('');
                      }
                    }}
                    style={{ color: requiresInsurance ? '#fb741a' : '#002D55' }}
                  />
                )}
                label="Require insurance support"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" disabled={!requiresInsurance && !insuranceNetwork} className={classes.fieldControl}>
                <InputLabel id="insurance-network-filter-label">Insurance network</InputLabel>
                <Select
                  labelId="insurance-network-filter-label"
                  label="Insurance network"
                  value={insuranceNetwork}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    setInsuranceNetwork(nextValue);
                    if (nextValue) {
                      setRequiresInsurance(true);
                    }
                  }}
                >
                  <MenuItem value="">Any supported insurer</MenuItem>
                  {insuranceCatalog.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={requiresPublicSubsidy}
                    onChange={(event) => setRequiresPublicSubsidy(event.target.checked)}
                    style={{ color: requiresPublicSubsidy ? '#fb741a' : '#002D55' }}
                  />
                )}
                label="Require public subsidy"
              />
            </Grid>
          </Grid>
        </Box>

        {loading && (
          <Box style={{ textAlign: 'center', padding: '32px 0' }}>
            <CircularProgress style={{ color: '#002D55' }} />
          </Box>
        )}

        {!loading && error && (
          <Card className={classes.resultCard}>
            <CardContent>
              <Typography variant="h6" style={{ color: '#002D55', marginBottom: 8 }}>
                Match step needs attention
              </Typography>
              <Typography variant="body2" style={{ color: '#486d86', marginBottom: 16 }}>
                {error}
              </Typography>
              <Button className={classes.secondaryButton} variant="outlined" onClick={() => window.location.assign('/')}>
                Back to landing page
              </Button>
            </CardContent>
          </Card>
        )}

        {!loading && !error && matches.length === 0 && (
          <Card className={classes.resultCard}>
            <CardContent>
              <Typography variant="h6" style={{ color: '#002D55', marginBottom: 8 }}>
                No clinics matched these criteria
              </Typography>
              <Typography variant="body2" style={{ color: '#486d86' }}>
                This is now a visible empty-state breakpoint you can test. Try widening the city or payer filters, or enable public listing and availability on more clinics.
              </Typography>
            </CardContent>
          </Card>
        )}

        {!loading && !error && matches.length > 0 && (
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Pick a clinic and continue to booking
          </Typography>
        )}

        <Grid container spacing={3}>
          {matches.map((clinic) => (
            <Grid item xs={12} md={6} key={clinic.id}>
              <Card className={classes.resultCard}>
                <CardContent style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h5" style={{ color: '#002D55', fontWeight: 700, marginBottom: 6 }}>
                    {clinic.name_business}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#486d86', marginBottom: 14 }}>
                    {clinic.city}, {clinic.country} · {clinic.care_provider_type}
                  </Typography>

                  <Box style={{ marginBottom: 14 }}>
                    {(clinic.specialities || []).map((speciality) => (
                      <Chip key={`${clinic.id}-${speciality}`} label={formatSpecialityLabel(speciality)} className={classes.chip} />
                    ))}
                  </Box>

                  <Typography variant="subtitle2" style={{ color: '#002D55', fontWeight: 700, marginBottom: 6 }}>
                    Earliest available slot
                  </Typography>
                  <Typography variant="body2" style={{ color: '#486d86', marginBottom: 14 }}>
                    {formatSlotDate(clinic.earliest_available_slot?.start_datetime)}
                    {clinic.earliest_available_slot?.clinic_location_name ? ` · ${clinic.earliest_available_slot.clinic_location_name}` : ''}
                  </Typography>

                  <Typography variant="subtitle2" style={{ color: '#002D55', fontWeight: 700, marginBottom: 6 }}>
                    Services
                  </Typography>
                  <Typography variant="body2" style={{ color: '#486d86', marginBottom: 20 }}>
                    {(clinic.services || []).length > 0
                      ? clinic.services.map((service) => service.name).join(', ')
                      : 'No public services listed yet.'}
                  </Typography>

                  <Box style={{ marginTop: 'auto' }}>
                    <Button
                      variant="contained"
                      className={classes.primaryButton}
                      onClick={() => handleContinueToBooking(clinic)}
                    >
                      Continue to booking handoff
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}

export default PublicProviderMatch;